import styled from 'styled-components';
import { useContext, useState, useEffect } from 'react';
import ButtonDiamondsLoading from 'components/common/buttons/button-diamonds-loading';
import { Icon } from 'components/common/icon/icon';
import { Link } from 'components/common/link';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { priceListPlans, steps } from 'utils/package-helper';
import { FormikProps } from 'formik';
import { TranslationContext } from 'contexts/translation-context';
import { JoinFormType, PackagePrices } from 'types/join';

import { Col, Row } from 'components/common/grid';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useSelector } from 'react-redux';
import { errorList, errorListTypes } from 'constants/error-list';
import { PRICE_LIST_TERMS_LINK, RAPNET_TERMS_LINK } from 'constants/urls';
import diamondLoaderGrey from 'assets/images/diamonds-row-grey.gif';
import { useLoading } from 'hooks';
import { Details } from './details';
import { NewsLettersMoreDetailsContent } from './news-letters-more-details-toggle';
import SmsTermsMoreDetailsContent from './sms-terms-more-details';
import { PlanState } from '../reducers/plan';

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  @media (max-width: 999px) {
    margin-top: 12px;
  }
`;
const NumberCircle = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid ${props => props.theme.colors.siteIndigo};
  color: ${props => props.theme.colors.siteIndigo};
  font-family: ${props => props.theme.fonts.interSansSemiBold};
  font-size: 14px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding-top: 1px;
  box-sizing: border-box;
`;
const SectionTitle = styled.div`
  font-family: ${props => props.theme.fonts.FunnelDisplay};
  font-size: 26px;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: ${props => props.theme.colors.black};
`;
const PlanWrapper = styled.div`
  margin-top: 24px;
  @media (max-width: 999px) {
    margin-top: 4px;
  }
`;
const ButtonsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 14px;
  flex-wrap: wrap;
`;

const LoadingStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
  font-family: ${props => props.theme.fonts.interSans};
  font-size: 13px;
  color: ${props => props.theme.colors.siteBlack};

  @media (max-width: 999px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 6px;
  }
`;

const LoadingDiamonds = styled.img`
  width: 110px;
  height: 30px;
  display: block;
`;

const ErrorIcon = styled('span')(({ theme }) => ({
  backgroundColor: (theme.palette as any).red,
  width: '16px',
  height: '16px',
  color: 'white',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '12px',
  fontWeight: 'bold',
  marginLeft: '4px'
}));

type CheckProps = { isVisible: boolean };

const Check = styled(Icon).attrs({
  name: 'check'
})<CheckProps>`
  color: #009ed3;
  font-size: 1.2rem;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.white};
  width: 12px;
  height: 12px;
  margin-left: -5px;
  visibility: ${props => (props.isVisible ? 'visible' : 'hidden')};
  margin-right: 3px;
`;

const StyledCol = styled(Col)`
  margin-right: 0;
  @media (max-width: 999px) {
    margin-right: 0;
  }
`;
const RowDetails = styled.div`
  width: 100%;
  background-color: transparent;
  padding: 8px 0 0;
  border: none;
  border-radius: 0;
`;
const CheckboxContainer = styled.div<{ disabled: boolean; isPriceList?: boolean }>`
  margin-top: ${props => (props.isPriceList ? '4px' : '0')};
  margin-left: 0;
  opacity: ${props => (props.disabled ? 0.5 : 1)};
  pointer-events: ${props => (props.disabled ? 'none' : 'auto')};

  & .MuiFormControlLabel-root {
    align-items: center;
    margin-left: 0;
    margin-right: 0;
    gap: 8px;
  }
  & .MuiCheckbox-root {
    color: ${props => props.theme.colors.siteIndigo};
    padding: 0;
  }
  & .MuiCheckbox-root.Mui-checked {
    color: ${props => props.theme.colors.siteIndigo};
  }
  & .MuiFormControlLabel-label {
    display: flex;
    align-items: center;
  }
`;

const CheckboxLabel = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 14px;
  line-height: 1.45;
  @media (max-width: 999px) {
    font-size: 12px;
  }
  white-space: nowrap;
  color: ${props => props.theme.colors.siteBlack};
  font-family: ${props => props.theme.fonts.interSans};
`;
const ConfirmButton = styled(ButtonDiamondsLoading)<{ isFreePlan?: boolean }>`
  width: ${props => (props.isFreePlan ? '140px' : '178px')};
  height: 40px;
  border-radius: 8px;
  margin-top: 0;
  background-color: ${props => props.theme.colors.siteIndigo} !important;
  border-color: ${props => props.theme.colors.siteIndigo} !important;
  color: ${props => props.theme.colors.white} !important;
  font-family: ${props => props.theme.fonts.interSansSemiBold} !important;
  font-size: 14px !important;

  &:hover,
  &:focus {
    background-color: ${props => props.theme.colors.siteIndigoHover} !important;
    border-color: ${props => props.theme.colors.siteIndigoHover} !important;
    color: ${props => props.theme.colors.white} !important;
    box-shadow: none !important;
  }
`;
const ButtonText = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  white-space: nowrap;
`;
const StyledLink = styled(Link)`
  text-decoration: none !important;
  font-size: 14px;
  font-family: ${props => props.theme.fonts.interSans};
  @media (max-width: 999px) {
    font-size: 12px;
  }
  margin-left: 4px;
  color: ${props => props.theme.colors.siteIndigo} !important;

  &:hover {
    text-decoration: underline !important;
  }
`;
const StyledCheckbox = styled(Checkbox)<{ error?: boolean }>`
  && {
    ${props =>
      props.error &&
      `
      & .MuiCheckbox-root {
        border: 2px solid ${props.theme.colors.red};
        border-radius: 4px; /* Optional: Adjust as needed */
      }
      
      & .MuiSvgIcon-root {
        color: ${props.theme.colors.red};
      }
    `};
    padding: 0;
    ${props =>
      props.error &&
      `
      & .registration-checkbox-icon {
        border-color: ${props.theme.colors.red};
      }
    `};
    & .MuiFormControlLabel-label {
      font-family: ${props => props.theme.fonts.interSansMedium};
    }
  }
`;
const CheckboxBox = styled.span.attrs({ className: 'registration-checkbox-icon' })`
  width: 16px;
  height: 16px;
  border: 1.5px solid ${props => props.theme.colors.siteIndigo};
  border-radius: 3px;
  background-color: ${props => props.theme.colors.white};
  box-sizing: border-box;
  display: inline-block;
`;
const CheckboxCheckedBox = styled(CheckboxBox)`
  background-color: ${props => props.theme.colors.siteIndigo};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    left: 4px;
    top: 1px;
    width: 5px;
    height: 9px;
    border: solid ${props => props.theme.colors.white};
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;
const Asterix = styled.span`
  color: ${props => props.theme.colors.red};
  margin-left: 3px;
  ::before {
    content: '*';
  }
`;
const Line = styled.div`
  width: 100%;
  margin-top: 18px;
  margin-bottom: 0;
  border-bottom: 1px solid ${props => props.theme.colors.siteCardBorder};
  display: none;
`;

const RefundPolicyNotice = styled.div`
  margin-top: 12px;
  margin-left: 5px;
  margin-right: 5px;
  font-size: 13px;
  font-family: ${props => props.theme.fonts.interSansBold};
  @media (max-width: 999px) {
    font-size: 11px;
  }
  color: #434343;
`;

type JoinFormProps = {
  form: FormikProps<JoinFormType>;
  isConfirmed: boolean;
  subscriptionId: string | null;
  numConfirmClicked: number;
  selectedPlan?: PackagePrices;
  isPriceList: boolean;
  finalTotalPrice: number;
};

export const JoinForm = ({
  form,
  isConfirmed,
  selectedPlan,
  subscriptionId,
  numConfirmClicked,
  isPriceList,
  finalTotalPrice
}: JoinFormProps) => {
  const TRANSLATION_TEXT = useContext(TranslationContext) as { [key: string]: string };
  const [proceedToPaymentClicked, setProceedToPaymentClicked] = useState(false);
  const isPriceListPlan = priceListPlans.includes(selectedPlan?.packageName || '');
  const termsAndConditionsLink = isPriceListPlan ? PRICE_LIST_TERMS_LINK : RAPNET_TERMS_LINK;
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [checkIsLoading] = useLoading();
  const { showErrorPopup } = useSelector((state: { plan: PlanState }) => state.plan) || {
    showErrorPopup: { show: false, errorMessage: null }
  };
  const paymentRequestIds = [
    'CREATE_SUBSCRIPTION_REQUEST',
    'UPDATE_SUBSCRIPTION_MEMBER_DETAILS',
    'GET_CURRENT_CONTRACT',
    'UPDATE_NEWS_LETTERS'
  ];
  const isPaymentRequestLoading = proceedToPaymentClicked || checkIsLoading(paymentRequestIds);

  // Listen for 504 errors and reset the button state
  useEffect(() => {
    if (showErrorPopup.errorMessage && showErrorPopup.errorMessage.includes('504')) {
      setProceedToPaymentClicked(false);
    }
  }, [showErrorPopup.errorMessage]);

  const handleFormSubmit = async (event: React.FormEvent) => {
    setProceedToPaymentClicked(true);
    event.preventDefault();
    try {
      // reCAPTCHA disabled - commented out
      // // Execute reCAPTCHA
      // if (!executeRecaptcha) {
      //   console.error('reCAPTCHA not loaded');
      //   setProceedToPaymentClicked(false);
      //   return;
      // }
      // const token = await executeRecaptcha('register'); // Action name is 'register'
      // form.setFieldValue('recaptcha', token);
      form.setFieldValue('recaptcha', '');
      form.submitForm();
      setProceedToPaymentClicked(false);
    } catch (error) {
      console.error(error);
      setProceedToPaymentClicked(false);
    }
  };

  return (
    <form onSubmit={handleFormSubmit} onReset={form.handleReset}>
      <PlanWrapper>
        <SectionHeader>
          <NumberCircle>2</NumberCircle>
          <SectionTitle>{TRANSLATION_TEXT.account_details || 'Account Details'}</SectionTitle>
        </SectionHeader>
        <StyledCol>
          <Details
            form={form}
            disabled={isConfirmed}
            isPriceList={isPriceList}
            selectedPlan={selectedPlan}
          />
          <RowDetails>
            {isPriceList && (
              <RefundPolicyNotice>
                Price List subscriptions are non-refundable. Once purchased, fees cannot be refunded
                or credited for unused time.
              </RefundPolicyNotice>
            )}
            <CheckboxContainer disabled={isConfirmed} isPriceList={isPriceList}>
              <FormControlLabel
                control={
                  <StyledCheckbox
                    name="agree"
                    checked={form.values.agree}
                    onChange={form.handleChange}
                    error={form.touched.agree && Boolean(form.errors.agree)}
                    icon={<CheckboxBox />}
                    checkedIcon={<CheckboxCheckedBox />}
                  />
                }
                label={
                  <CheckboxLabel>
                    {isPriceListPlan
                      ? TRANSLATION_TEXT.agree_to_rapaport
                      : TRANSLATION_TEXT.agree_to_rapnet}
                    <StyledLink href={termsAndConditionsLink} external newTab>
                      {TRANSLATION_TEXT.terms_and_conditions}
                    </StyledLink>
                    <Asterix />
                    {form.touched.agree && Boolean(form.errors.agree) && <ErrorIcon>!</ErrorIcon>}
                  </CheckboxLabel>
                }
              />
            </CheckboxContainer>
            <SmsTermsMoreDetailsContent form={form} disabled={isConfirmed} />
            <NewsLettersMoreDetailsContent form={form} disabled={isConfirmed} />
          </RowDetails>
          <ButtonsRow>
            <ConfirmButton
              submit
              ids={paymentRequestIds}
              disabled={isConfirmed || isPaymentRequestLoading}
              loadingText=""
              hideLoadingIndicator
              errorClickable={!subscriptionId && numConfirmClicked > 1}
              isVertical={window.innerWidth <= 999}
              isFreePlan={finalTotalPrice === 0}
            >
              <ButtonText>
                {isConfirmed && <Check isVisible={isConfirmed} />}
                {isConfirmed
                  ? TRANSLATION_TEXT.order_confirmed
                  : finalTotalPrice > 0
                    ? TRANSLATION_TEXT.set_up_payment ||
                      TRANSLATION_TEXT.proceed_to_payment ||
                      'Set Up Payment'
                    : 'Continue'}
              </ButtonText>
            </ConfirmButton>
          </ButtonsRow>
          {isPaymentRequestLoading && (
            <LoadingStatus>
              <LoadingDiamonds src={diamondLoaderGrey} alt="" />
              <span>{TRANSLATION_TEXT.polishing_your_membership_request}</span>
            </LoadingStatus>
          )}

          <Line />
        </StyledCol>
      </PlanWrapper>
    </form>
  );
};
